import React from "react";
import { Form, Formik, FormikProps } from "formik";
import { Button, TextField, Box, Grid } from "@mui/material";
import { styled } from '@mui/material/styles';
import { ReminderFilter, parseFilter } from "../../core/api/reminder/ReminderModel";

import qs from "qs";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import ClientStateSelectInput from "../classifier/ClientStateSelectInput";
import { DatePicker } from "../common/DatePicker";
import { translations } from "../../translations/common";

const RootForm = styled(Form)({
  width: '100%',
});

const MarginContainer = styled(Grid)(({ theme }) => ({
  margin: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const DatePickerContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const StyledClientStateSelect = styled(ClientStateSelectInput)(({ theme }) => ({
  margin: theme.spacing(1),
  width: '100%',
}));

const InputsContainer = styled(Box)({
  width: '100%',
  '& .MuiInputBase-root': { width: '100%' },
  '& .MuiOutlinedInput-root': { width: '100%' },
  '& .MuiFormControl-root': { width: '100%' }
});

const ReminderFilterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const filter = parseFilter(location.search)
  
  const emptyFilter: ReminderFilter = {
    dateFrom: '',
    dateTo: '',
    tableFilter: '',
    companyFilter: '',
    clientState: '',
  };

  const onClear = (formikProps: FormikProps<ReminderFilter>) => {
    // Reset form values
    formikProps.resetForm({ values: emptyFilter });
    
    // Clear URL parameters
    navigate({
      pathname: location.pathname,
      search: '',
    }, { replace: true });
  };

  return (
    <Formik
      initialValues={filter}
      enableReinitialize
      onSubmit={(values, actions) => {
          navigate({
          search: qs.stringify(values, {
            serializeDate: (date: Date) => {
              return format(date, "yyyy-MM-dd");
            },
          }),
        });
        actions.setSubmitting(false);
      }}
    >
      {(props: FormikProps<ReminderFilter>) => {
        const { values, handleChange, isSubmitting, errors } = props;
        return (
          <RootForm>
            <InputsContainer>
              <MarginContainer
                container
                direction="column"
                justifyContent="center"
                alignItems="stretch"
                spacing={1}
              >
                <Grid size={12}>
                  <StyledTextField
                    name="companyFilter"
                    id="companyFilter"
                    value={values.companyFilter}
                    label={translations.reminders.filter.companyFilter}
                    fullWidth
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <DatePickerContainer>
                    <DatePicker
                      id="dateFrom"
                      name="dateFrom"
                      label={translations.reminders.filter.dateFrom}
                      value={values.dateFrom}
                      onChange={handleChange}
                      error={!!errors.dateFrom}
                      helperText={errors.dateFrom}
                      fullWidth
                    />
                  </DatePickerContainer>
                </Grid>
                <Grid size={12}>
                  <DatePickerContainer>
                    <DatePicker
                      id="dateTo"
                      name="dateTo"
                      label={translations.reminders.filter.dateTo}
                      value={values.dateTo}
                      onChange={handleChange}
                      error={!!errors.dateTo}
                      helperText={errors.dateTo}
                      fullWidth
                    />
                  </DatePickerContainer>
                </Grid>
                <Grid size={12}>
                  <StyledClientStateSelect
                    name="clientState"
                    fullWidth
                    id="user-type-select"
                    value={values.clientState}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={12}>
                  <StyledButton
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    color="primary"
                    fullWidth
                  >
                    {translations.common.filter}
                  </StyledButton>
                  <StyledButton
                    type="button"
                    variant="contained"
                    color="secondary"
                    onClick={() => onClear(props)}
                    fullWidth
                  >
                    {translations.search.clearLabel}
                  </StyledButton>
                </Grid>
              </MarginContainer>
            </InputsContainer>
          </RootForm>
        );
      }}
    </Formik>
  );
};

export default ReminderFilterForm;
